import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../common/user";
import { documentsService } from "../../../services/admin/documents.service";
import {
	setCreateCreativeState,
	setGeneralProgressHide,
	setGeneralProgressShow,
	setGeneralSnackbarState
} from "../../common/ui";


export type payloadList = {
	page: string | number | undefined;
	perPage: string | number | undefined;
	platform: string | number | undefined;
	order: any;
};

export type columnsItem = {
  name: string;
  type: string;
  sort: boolean;
  match?: boolean;
};

export type listItem = {
  [key: string]: any;
};

export interface ITableData {
  list: listItem[];
  columns: columnsItem[];
	totalEntries: number;
	startRowNum: number;
	totalPages: number;
	endRowNum: number;
}

export type documentsSliceState = {
  list: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
	updateDocument: {
    data: any,
    error: any,
    isFetching: boolean,
  },
	commonList: {
    data: any,
    error: any,
    isFetching: boolean,
  },
	content: {
    data: any,
    error: any,
    isFetching: boolean,
  },
};

const initialState: documentsSliceState = {
  list: {
    data: null,
    error: null,
    isFetching: false,
  },
	updateDocument: {
    data: null,
    error: null,
    isFetching: false,
  },
	commonList: {
    data: null,
    error: null,
    isFetching: false,
  },
	content: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getListData: any = createAsyncThunk(
  'admin/documents/geListData',
  async (payload: payloadList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await documentsService.getList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const updateDocumentData: any = createAsyncThunk(
	'admin/documents/updateDocumentData',
	async (payload: payloadList, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await documentsService.updateDocument(payload);
			const data = await response.json();

			if (data.hasOwnProperty('authorized') && data.authorized === false) {
				dispatch(setUser(null));
				localStorage.removeItem('user');
			}

			if (response.ok && response.status === 200) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'success',
						message: 'success',
						messageKey: 'common.messages.success',
					})
				);
				dispatch(setCreateCreativeState(false));
			}

			if (!response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'warning',
						message: 'error',
						messageKey: 'common.messages.error',
					})
				);
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(
				setGeneralSnackbarState({
					open: true,
					type: 'warning',
					message: 'error',
					messageKey: 'common.messages.error',
				})
			);
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const getCommonListData: any = createAsyncThunk(
	'admin/documents/getCommonListData',
	async (payload: payloadList, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await documentsService.getCommonList(payload);
			const data = await response.json();

			if (data.hasOwnProperty('authorized') && data.authorized === false) {
				dispatch(setUser(null));
				localStorage.removeItem('user');
			}

			if (!response.ok) {
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const getContentData: any = createAsyncThunk(
	'admin/documents/getContentData',
	async (payload: payloadList, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await documentsService.getContent(payload);
			const data = await response.json();

			if (data.hasOwnProperty('authorized') && data.authorized === false) {
				dispatch(setUser(null));
				localStorage.removeItem('user');
			}

			if (!response.ok) {
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const documentsSlice = createSlice({
  name: 'documents',
  initialState: initialState,
  reducers: {
    clearListState(state) {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = null;
    },
	  clearUpdateDocumentState(state) {
      state.updateDocument.error = null;
      state.updateDocument.isFetching = false;
      state.updateDocument.data = null;
    },
	  clearCommonListState(state) {
		  state.commonList.error = null;
		  state.commonList.isFetching = false;
		  state.commonList.data = null;
	  },
	  clearContentDocumentState(state) {
      state.content.error = null;
      state.content.isFetching = false;
      state.content.data = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<documentsSliceState>>) => {
    builder.addCase(getListData.pending, (state ) => {
      state.list.isFetching = true;
      state.list.error = null;
    });
    builder.addCase(getListData.fulfilled, (state , action) => {
      state.list.error = null;
      state.list.isFetching = false;
	    state.list.data = action.payload;
    });
    builder.addCase(getListData.rejected, (state , action) => {
      state.list.isFetching = false;
      state.list.error = action.payload;
      state.list.data = null;
    });

		builder.addCase(updateDocumentData.pending, (state ) => {
      state.updateDocument.isFetching = true;
      state.updateDocument.error = null;
    });
    builder.addCase(updateDocumentData.fulfilled, (state , action) => {
      state.updateDocument.error = null;
      state.updateDocument.isFetching = false;
	    state.updateDocument.data = action.payload;
    });
    builder.addCase(updateDocumentData.rejected, (state , action) => {
      state.updateDocument.isFetching = false;
      state.updateDocument.error = action.payload;
      state.updateDocument.data = null;
    });

		builder.addCase(getCommonListData.pending, (state ) => {
      state.commonList.isFetching = true;
      state.commonList.error = null;
    });
    builder.addCase(getCommonListData.fulfilled, (state , action) => {
      state.commonList.error = null;
      state.commonList.isFetching = false;
	    state.commonList.data = action.payload;
    });
    builder.addCase(getCommonListData.rejected, (state , action) => {
      state.commonList.isFetching = false;
      state.commonList.error = action.payload;
      state.commonList.data = null;
    });

		builder.addCase(getContentData.pending, (state ) => {
      state.content.isFetching = true;
      state.content.error = null;
    });
    builder.addCase(getContentData.fulfilled, (state , action) => {
      state.content.error = null;
      state.content.isFetching = false;
	    state.content.data = action.payload;
    });
    builder.addCase(getContentData.rejected, (state , action) => {
      state.content.isFetching = false;
      state.content.error = action.payload;
      state.content.data = null;
    });
  },
});
export const {
  clearListState,
	clearUpdateDocumentState,
	clearCommonListState,
	clearContentDocumentState,
} = documentsSlice.actions;
export default documentsSlice.reducer;
